import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ChartLoaderWrapperComponent } from '@enginuity/analytics/organisms/analytics-loader/analytics-loading-wrapper.component';
import { AnalyticsTrendWidgetComponent } from '@enginuity/analytics/organisms/analytics-trend-widget/analytics-trend-widget.component';
import { CustomBarChartContainerComponent } from '@enginuity/analytics/organisms/custom-bar-chart-container/custom-bar-chart-container.component';
import { HighChartContainerComponent } from '@enginuity/analytics/organisms/high-chart-container/high-chart-container.component';
import { GlobalLoaderWrapperComponent } from '@enginuity/analytics/organisms/global-loader-wrapper/global-loader-wrapper.component';
import { AnalyticsRangeContext } from '@services/analytics-services/core';
import { ShippingAnalyticsCharts } from '@services/analytics-services/shipping/shipping-analytics.charts';
import { ProcessingState } from '@services/core-services/models';
import { Subject, takeUntil, switchMap, forkJoin } from 'rxjs';
import { DashboardStore } from '../dashboard/dashboard.store';
import { DashboardShipmentStore } from './dashboard-shipment.store';

// Define a generic ChartOptions type for component
interface ChartOptions {
  data?: any[];
  title?: string;
  tooltipFormatter?: (point: any) => string;
  [key: string]: any;
}

@Component({
  selector: 'app-dashboard-shipment',
  imports: [
    AnalyticsTrendWidgetComponent,
    AsyncPipe,
    HighChartContainerComponent,
    CustomBarChartContainerComponent,
    ChartLoaderWrapperComponent,
    GlobalLoaderWrapperComponent,
  ],
  templateUrl: './dashboard-shipment.component.html',
  styleUrl: './dashboard-shipment.component.scss',
  providers: [DashboardShipmentStore, ShippingAnalyticsCharts],
})
export class DashboardShipmentComponent implements OnDestroy, OnInit {
  // Get signals directly from the store
  protected rangeContext = this.dashboardStore.getRangeContextSignal();

  // Create resources using the new resource pattern
  protected shipmentAnalyticsResource = this.dashboardShipmentStore.createShipmentAnalyticsResource(
    this.rangeContext
  );

  protected shippingSLAChartResource = this.dashboardShipmentStore.createShippingSLAChartResource(
    this.rangeContext
  );

  protected shipmentsByCourierResource =
    this.dashboardShipmentStore.createShipmentsByCourierResource(this.rangeContext);

  protected averageDeliveryTimeResource =
    this.dashboardShipmentStore.createAverageDeliveryTimeResource(this.rangeContext);

  protected context: AnalyticsRangeContext | undefined = undefined;

  protected stateWiseShipmentPerformanceStats$ =
    this.dashboardShipmentStore.getStateWiseShipmentPerformanceStats();
  protected stateWiseShipmentPerformanceTabs$ =
    this.dashboardShipmentStore.getStateWiseShipmentPerformanceTabs();
  protected stateWiseShipmentPerformanceTabsActiveTab$ =
    this.dashboardShipmentStore.getStateWiseShipmentPerformanceTabsActiveTab();
  protected stateWiseShipmentPerformancePagination$ =
    this.dashboardShipmentStore.getStateWiseShipmentPerformancePagination();
  protected stateWiseShipmentPerformanceLoader$ =
    this.dashboardShipmentStore.getStateWiseShipmentPerformanceLoader();

  private destroy$ = new Subject<void>();

  // Title arrays for different chart sections
  protected stateWiseShipmentPerformanceTitles: string[] = [
    'Shipped',
    'Early',
    'On time',
    'Late',
    'Shipping time',
    'Transit time',
    'Delivery time',
  ];

  protected formatTooltipValue = this.dashboardShipmentStore.formatTooltipValue;
  protected isLoading = (state: ProcessingState | null): boolean =>
    state === ProcessingState.Loading;

  protected getDefaultChartOptions = (): ChartOptions => ({});

  constructor(
    protected readonly dashboardShipmentStore: DashboardShipmentStore,
    private readonly dashboardStore: DashboardStore
  ) {}

  ngOnInit() {
    this.dashboardStore
      .getRangeContext()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(context => {
          this.context = context;
          if (context) {
            // Use forkJoin to execute all API calls in parallel
            // switchMap will cancel previous forkJoin if a new context is emitted
            return forkJoin([
              this.dashboardShipmentStore.loadStateWiseShipmentPerformanceData(context),
            ]);
          }
          return [];
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onStateWiseShipmentPerformanceTabChange(tabId: string): void {
    this.dashboardShipmentStore.updateStatePerformanceDefaultTab(tabId);
  }

  protected onStateWiseShipmentPerformancePageChange(event: {
    tabId: string;
    page: number;
    limit: number;
  }): void {
    this.dashboardShipmentStore.updateStatePerformancePagination(
      event.tabId,
      event.page,
      event.limit
    );
  }
}
